import React, { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import Navbar from './Navbar'
import { events } from '../utils/analytics'
import { PageSEO, softwareAppSchema } from '../utils/seo'

/* ─── Reveal wrapper ─────────────────────────────────────────── */
function Reveal({ children, delay = 0, y = 32 }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  )
}

/* Link interno o esterno a seconda dell'indirizzo: i case study rimandano al sito del cliente. */
function Cta({ to, children, ...props }) {
  return to.startsWith('http')
    ? <a href={to} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
    : <Link to={to} {...props}>{children}</Link>
}

/*
 * Template condiviso delle pagine prodotto (Veyro, Veyro Trainer, …) e dei case study.
 * Opzionali: `about` (blocco "cos'è" / problema), `faq`, `links` (pagine legali), `hero.image`
 * (+ `imageWidth`/`imageHeight`/`imageMaxWidth`), `gallery` (banner + screenshot 520×1131 in riga scorrevole),
 * `seo.jsonLd` (sostituisce lo schema SoftwareApplication), `seo.ogImage` (URL assoluto).
 * Tutto il contenuto — testi, colori, link legali, cross-link — arriva da `data`,
 * definito nel componente della singola pagina.
 */
export default function ProductPage({ data }) {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  const {
    slug, accent, accent2, seo, eyebrow, badge, hero,
    stats, features, stack, cta, links, related, about, faq, gallery,
  } = data

  const gradientText = {
    background: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  }
  const eyebrowStyle = {
    fontFamily: 'var(--font-mono)', fontSize: '0.78rem', letterSpacing: '0.12em',
    color: accent2, textTransform: 'lowercase',
  }
  const footerLinkStyle = {
    fontFamily: 'var(--font-display)', fontSize: '0.82rem',
    color: 'var(--color-muted)', textDecoration: 'none',
    transition: 'color 0.2s',
  }

  return (
    <>
      <PageSEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        ogImage={seo.ogImage}
        jsonLd={seo.jsonLd || softwareAppSchema({ image: hero.image, ...seo.schema })}
      />
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: 'var(--color-bg)', minHeight: '100vh', fontFamily: 'var(--font-display)' }}
      >
        {/* ── Hero ──────────────────────────────────────────── */}
        <section style={{ position: 'relative', overflow: 'hidden', paddingTop: '7rem', paddingBottom: '6rem' }}>

          <div style={{
            position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)',
            width: '700px', height: '480px',
            background: `radial-gradient(ellipse at center, ${accent}20 0%, transparent 70%)`,
            pointerEvents: 'none', zIndex: 0,
          }} />
          <div style={{
            position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
            backgroundImage: `linear-gradient(${accent}0a 1px, transparent 1px), linear-gradient(90deg, ${accent}0a 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
          }} />

          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '3rem', alignItems: 'center' }}>

              <div>
                <motion.p
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{ ...eyebrowStyle, marginBottom: '1.5rem' }}
                >
                  {eyebrow}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.18 }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}
                >
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                    background: `${accent}1a`, border: `1px solid ${accent}4d`,
                    borderRadius: '100px', padding: '0.3rem 0.9rem',
                    fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: accent2, letterSpacing: '0.08em',
                  }}>
                    <span style={{
                      width: '6px', height: '6px', borderRadius: '50%', background: accent,
                      boxShadow: `0 0 8px ${accent}`,
                      animation: `pulse-dot-${slug} 2s ease-in-out infinite`,
                    }} />
                    {badge}
                  </span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    fontFamily: 'var(--font-display)', fontWeight: 800,
                    fontSize: 'clamp(2.2rem, 5.5vw, 4rem)',
                    lineHeight: 1.15, color: 'var(--color-text)',
                    marginBottom: '1.5rem', letterSpacing: '-0.02em',
                  }}
                >
                  {hero.titleLead}{' '}
                  <span style={gradientText}>{hero.titleAccent}</span>
                </motion.h1>

                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.38 }}
                  style={{
                    fontSize: 'clamp(1rem, 2vw, 1.18rem)', color: 'var(--color-text-2)',
                    maxWidth: '520px', margin: '0 0 2.5rem', lineHeight: 1.7,
                  }}
                >
                  {hero.intro}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
                >
                  <Cta to={hero.ctaTo} className="btn-primary" onClick={() => events.productCta(slug)}>
                    {hero.ctaLabel}
                  </Cta>
                  {links && (
                    <Link to={links.privacy} className="btn-secondary">
                      Privacy Policy
                    </Link>
                  )}
                </motion.div>

                {related && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.62 }}
                    style={{ marginTop: '1.5rem' }}
                  >
                    <Link
                      to={related.to}
                      style={{
                        fontFamily: 'var(--font-mono)', fontSize: '0.8rem',
                        color: accent2, textDecoration: 'none',
                        borderBottom: `1px solid ${accent}59`, paddingBottom: '2px',
                      }}
                    >
                      {related.label} →
                    </Link>
                  </motion.div>
                )}
              </div>

              {hero.image && (
              <motion.div
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                style={{ position: 'relative' }}
              >
                <div style={{
                  position: 'absolute', inset: '-10%',
                  background: `radial-gradient(ellipse at center, ${accent}25 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />
                <img
                  src={hero.image}
                  alt={hero.imageAlt}
                  width={hero.imageWidth || 640}
                  height={hero.imageHeight || 1203}
                  style={{
                    position: 'relative', width: '100%', height: 'auto', maxWidth: hero.imageMaxWidth || '320px', margin: '0 auto',
                    display: 'block', borderRadius: '32px',
                    border: `1px solid ${accent}40`,
                    boxShadow: `0 30px 80px ${accent}25, 0 8px 32px rgba(0,0,0,0.5)`,
                  }}
                />
              </motion.div>
              )}
            </div>
          </div>
        </section>

        {/* ── Stats ─────────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
          <div className="container">
            <Reveal>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '1.25rem',
              }}>
                {stats.map((s, i) => (
                  <Reveal key={s.label} delay={i * 0.1}>
                    <div style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderTop: `2px solid ${s.color}`,
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '2rem 1.75rem',
                      textAlign: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '60px',
                        background: `radial-gradient(ellipse at 50% 0%, ${s.color}18 0%, transparent 70%)`,
                        pointerEvents: 'none',
                      }} />
                      <div style={{ fontSize: '1.8rem', marginBottom: '0.75rem' }}>{s.icon}</div>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontWeight: 800,
                        fontSize: '2.6rem', color: s.color, lineHeight: 1,
                        letterSpacing: '-0.03em', marginBottom: '0.5rem',
                      }}>
                        {s.value}
                      </div>
                      <div style={{
                        fontFamily: 'var(--font-display)', fontSize: '0.85rem',
                        color: 'var(--color-muted)', letterSpacing: '0.02em',
                      }}>
                        {s.label}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── About (opzionale) ─────────────────────────────── */}
        {about && (
          <section className="section" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
            <div className="container">
              <Reveal>
                <p style={{ ...eyebrowStyle, marginBottom: '0.75rem' }}>{about.eyebrow}</p>
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)',
                  color: 'var(--color-text)', letterSpacing: '-0.02em',
                  marginBottom: '1.25rem',
                }}>
                  {about.title}
                </h2>
                {about.text.map(par => (
                  <p key={par} style={{
                    color: 'var(--color-text-2)', fontSize: '1.05rem',
                    maxWidth: '68ch', lineHeight: 1.75, marginBottom: '1rem',
                  }}>
                    {par}
                  </p>
                ))}
              </Reveal>
            </div>
          </section>
        )}

        {/* ── Gallery (opzionale): banner + screenshot scorrevoli ── */}
        {gallery && (
          <section className="section" style={{ paddingTop: '3rem', paddingBottom: '2rem' }}>
            <div className="container">
              <Reveal>
                <p style={{ ...eyebrowStyle, marginBottom: '0.75rem' }}>{gallery.eyebrow || '// screenshot'}</p>
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)',
                  color: 'var(--color-text)', letterSpacing: '-0.02em',
                  marginBottom: '2rem',
                }}>
                  {gallery.title}
                </h2>
                {gallery.banner && (
                  <img
                    src={gallery.banner.src}
                    alt={gallery.banner.alt}
                    width={gallery.banner.width}
                    height={gallery.banner.height}
                    loading="lazy"
                    style={{
                      width: '100%', height: 'auto', display: 'block', borderRadius: '24px',
                      border: `1px solid ${accent}33`,
                      boxShadow: `0 30px 80px ${accent}1f, 0 8px 32px rgba(0,0,0,0.5)`,
                      marginBottom: '2.5rem',
                    }}
                  />
                )}
              </Reveal>
              <Reveal delay={0.1}>
                <div
                  tabIndex={0}
                  aria-label={`${gallery.title} — scorri per vedere tutte le schermate`}
                  style={{
                    display: 'flex', gap: '1.25rem', overflowX: 'auto',
                    scrollSnapType: 'x mandatory', paddingBottom: '1rem',
                    scrollbarColor: `${accent}66 transparent`,
                  }}
                >
                  {gallery.screens.map(s => (
                    <img
                      key={s.src}
                      src={s.src}
                      alt={s.alt}
                      width={520}
                      height={1131}
                      loading="lazy"
                      style={{
                        flex: '0 0 auto', width: 'min(240px, 62vw)', height: 'auto',
                        scrollSnapAlign: 'start', borderRadius: '20px',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    />
                  ))}
                </div>
              </Reveal>
            </div>
          </section>
        )}

        {/* ── Features ──────────────────────────────────────── */}
        <section className="section">
          <div className="container">
            <Reveal>
              <p style={{ ...eyebrowStyle, marginBottom: '0.75rem' }}>
                {features.eyebrow || '// funzionalità'}
</p>
              <h2 style={{
                fontFamily: 'var(--font-display)', fontWeight: 800,
                fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)',
                color: 'var(--color-text)', letterSpacing: '-0.02em',
                marginBottom: '0.75rem',
              }}>
                {features.title}
              </h2>
              <p style={{
                color: 'var(--color-muted)', fontSize: '1rem',
                maxWidth: '520px', lineHeight: 1.65, marginBottom: '3.5rem',
              }}>
                {features.lead}
              </p>
            </Reveal>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.25rem',
            }}>
              {features.items.map((f, i) => (
                <Reveal key={f.title} delay={i * 0.08}>
                  <div
                    style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderTop: `2px solid ${accent}59`,
                      backdropFilter: 'blur(20px)',
                      borderRadius: '20px',
                      padding: '2rem',
                      height: '100%',
                      transition: 'border-color 0.25s, transform 0.25s',
                      cursor: 'default',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderTopColor = accent
                      e.currentTarget.style.transform = 'translateY(-3px)'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderTopColor = `${accent}59`
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                  >
                    <div style={{
                      fontSize: '1.6rem', marginBottom: '1rem',
                      filter: `drop-shadow(0 0 8px ${accent}66)`,
                    }}>
                      {f.icon}
                    </div>
                    <h3 style={{
                      fontFamily: 'var(--font-display)', fontWeight: 700,
                      fontSize: '1.05rem', color: 'var(--color-text)',
                      marginBottom: '0.6rem', letterSpacing: '-0.01em',
                    }}>
                      {f.title}
                    </h3>
                    <p style={{
                      color: 'var(--color-muted)', fontSize: '0.9rem', lineHeight: 1.7,
                    }}>
                      {f.desc}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ── Stack ─────────────────────────────────────────── */}
        <section className="section" style={{ paddingTop: '2rem' }}>
          <div className="container">
            <Reveal>
              <p style={{ ...eyebrowStyle, marginBottom: '1.5rem' }}>
                {'// stack tecnologico'}
</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.65rem' }}>
                {stack.map((tech, i) => (
                  <Reveal key={tech} delay={i * 0.06} y={12}>
                    <span className="stack-badge">{tech}</span>
                  </Reveal>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── FAQ (opzionale) ───────────────────────────────── */}
        {faq && (
          <section className="section" style={{ paddingTop: '2rem' }}>
            <div className="container">
              <Reveal>
                <p style={{ ...eyebrowStyle, marginBottom: '0.75rem' }}>{'// domande frequenti'}</p>
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)',
                  color: 'var(--color-text)', letterSpacing: '-0.02em',
                  marginBottom: '2rem',
                }}>
                  In breve.
                </h2>
              </Reveal>
              <div style={{ display: 'grid', gap: '1rem', maxWidth: '760px' }}>
                {faq.map((item, i) => (
                  <Reveal key={item.q} delay={i * 0.06}>
                    <div style={{
                      background: 'rgba(13,13,24,0.8)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderLeft: `2px solid ${accent}`,
                      borderRadius: '14px', padding: '1.4rem 1.6rem',
                    }}>
                      <h3 style={{
                        fontFamily: 'var(--font-display)', fontWeight: 700,
                        fontSize: '1.05rem', color: 'var(--color-text)', marginBottom: '0.5rem',
                      }}>
                        {item.q}
                      </h3>
                      <p style={{ color: 'var(--color-text-2)', fontSize: '0.95rem', lineHeight: 1.7 }}>
                        {item.a}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── CTA finale ────────────────────────────────────── */}
        <section className="section">
          <div className="container">
            <Reveal>
              <div style={{
                background: 'rgba(13,13,24,0.8)',
                border: `1px solid ${accent}33`,
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                padding: 'clamp(2.5rem, 6vw, 4rem)',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute', bottom: '-30%', left: '50%', transform: 'translateX(-50%)',
                  width: '500px', height: '300px',
                  background: `radial-gradient(ellipse at center, ${accent}26 0%, transparent 70%)`,
                  pointerEvents: 'none',
                }} />

                <p style={{ ...eyebrowStyle, marginBottom: '1.25rem', position: 'relative' }}>
                  {cta.eyebrow}
                </p>
                <h2 style={{
                  fontFamily: 'var(--font-display)', fontWeight: 800,
                  fontSize: 'clamp(1.9rem, 4vw, 3rem)',
                  color: 'var(--color-text)', letterSpacing: '-0.02em',
                  marginBottom: '1rem', position: 'relative',
                }}>
                  {cta.titleLead}{' '}
                  <span style={gradientText}>{cta.titleAccent}</span>
                </h2>
                <p style={{
                  color: 'var(--color-muted)', fontSize: '1rem', lineHeight: 1.7,
                  maxWidth: '480px', margin: '0 auto 2.5rem', position: 'relative',
                }}>
                  {cta.text}
                </p>

                <div style={{
                  display: 'flex', gap: '1rem', justifyContent: 'center',
                  flexWrap: 'wrap', position: 'relative',
                }}>
                  <Cta to={cta.to} className="btn-primary" onClick={() => events.productCta(slug)}>
                    {cta.label}
                  </Cta>
                  {links && (
                    <Link to={links.terms} className="btn-secondary">
                      Termini d'uso
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── Footer ────────────────────────────────────────── */}
        <footer style={{
          borderTop: '1px solid rgba(255,255,255,0.06)',
          padding: '2.5rem 0',
        }}>
          <div className="container" style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            justifyContent: 'space-between', gap: '1rem',
          }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontWeight: 700,
              fontSize: '0.9rem', color: 'var(--color-text-2)',
            }}>
              VMDProject
            </span>

            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <Link to="/progetti" style={footerLinkStyle}
                onMouseEnter={e => e.currentTarget.style.color = accent2}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
              >
                Tutti i progetti
              </Link>
              {links && (
                <>
                  <Link to={links.privacy} style={footerLinkStyle}
                    onMouseEnter={e => e.currentTarget.style.color = accent2}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
                  >
                    Privacy
                  </Link>
                  <Link to={links.terms} style={footerLinkStyle}
                    onMouseEnter={e => e.currentTarget.style.color = accent2}
                    onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
                  >
                    Termini
                  </Link>
                </>
              )}
              {related && (
                <Link to={related.to} style={footerLinkStyle}
                  onMouseEnter={e => e.currentTarget.style.color = accent2}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--color-muted)'}
                >
                  {related.footerLabel || related.label}
                </Link>
              )}
            </div>

            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
              color: 'var(--color-muted)', letterSpacing: '0.04em',
            }}>
              © 2026
            </span>
          </div>
        </footer>

      </motion.main>

      <style>{`
        @keyframes pulse-dot-${slug} {
          0%, 100% { opacity: 1; box-shadow: 0 0 8px ${accent}; }
          50%       { opacity: 0.5; box-shadow: 0 0 4px ${accent}; }
        }
      `}</style>
    </>
  )
}
